import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Zap, RefreshCw, Plus, Trash2, Info } from "lucide-react";
import { getStoreId } from "../utils/auth";
import { showToast } from "../utils/toast";
import InstantSiteReconnectBanner, {
  isInstantSiteAuthError,
} from "./InstantSiteReconnectBanner";

const apiUrl = import.meta.env.VITE_BACKEND_URL;

function planLabel(accountType, allowed) {
  if (!allowed) return "Free Trial plan";
  const t = String(accountType || "").toLowerCase();
  if (t.startsWith("enterprise")) return "Enterprise plan";
  if (t.startsWith("pro")) return "Pro plan";
  if (t.startsWith("basic")) return "Basic plan";
  return "Paid plan";
}

function sourceLabel(reason) {
  if (!reason) return "System";
  if (reason === "manual") return "Manual";
  if (reason.startsWith("product")) return "Product";
  if (reason.startsWith("category")) return "Category";
  if (reason.startsWith("enable")) return "Setup";
  return reason;
}

function statusTone(status) {
  if (status === "active") return "success";
  if (status === "error") return "danger";
  if (status === "disabled") return "secondary";
  return "warning";
}

export default function IndexNow({ onOpenPricing }) {
  const hashSiteId = getStoreId();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [storefrontKeyUrl, setStorefrontKeyUrl] = useState(null);
  const [allowed, setAllowed] = useState(false);
  const [accountType, setAccountType] = useState("trial");
  const [tab, setTab] = useState("queued");
  const [queued, setQueued] = useState([]);
  const [queuedTotal, setQueuedTotal] = useState(0);
  const [submitted, setSubmitted] = useState([]);
  const [submittedTotal, setSubmittedTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manualText, setManualText] = useState("");
  const [instantSiteHint, setInstantSiteHint] = useState(null);

  const api = useCallback(
    (path, opts) => {
      const base = `${apiUrl}/api/indexnow${path}`;
      const sep = base.includes("?") ? "&" : "?";
      return fetch(`${base}${sep}hashSiteId=${encodeURIComponent(hashSiteId)}`, {
        ...opts,
        headers: { "Content-Type": "application/json", ...(opts?.headers || {}) },
      });
    },
    [hashSiteId],
  );

  const loadSettings = useCallback(async () => {
    const res = await api("/settings");
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to load settings");
    setSettings(data.settings);
    setStorefrontKeyUrl(data.storefrontKeyUrl || null);
    setAllowed(Boolean(data.allowed));
    setAccountType(String(data.accountType || "trial"));
    const lastErr = data.settings?.lastError || null;
    if (isInstantSiteAuthError(lastErr)) {
      setInstantSiteHint(lastErr);
    }
  }, [api]);

  const loadQueue = useCallback(async () => {
    const offset = page * pageSize;
    const res = await api(`/queue?limit=${pageSize}&offset=${offset}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to load queue");
    setQueued(Array.isArray(data.items) ? data.items : []);
    setQueuedTotal(Number(data.total || 0));
    setSelectedIds([]);
  }, [api, page, pageSize]);

  const loadHistory = useCallback(async () => {
    const offset = page * pageSize;
    const res = await api(`/history?limit=${pageSize}&offset=${offset}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to load history");
    setSubmitted(Array.isArray(data.items) ? data.items : []);
    setSubmittedTotal(Number(data.total || 0));
  }, [api, page, pageSize]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await loadSettings();
      if (tab === "queued") await loadQueue();
      else await loadHistory();
    } catch (err) {
      showToast(err.message || "Failed to refresh", "error");
    } finally {
      setLoading(false);
    }
  }, [loadHistory, loadQueue, loadSettings, tab]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
  }, [tab, pageSize]);

  const pageCount = useMemo(() => {
    const total = tab === "queued" ? queuedTotal : submittedTotal;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [pageSize, queuedTotal, submittedTotal, tab]);

  const handleEnable = async () => {
    if (!allowed) { onOpenPricing?.(); return; }
    setBusy(true);
    try {
      const res = await api("/enable", { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        if (data.upgradeRequired) onOpenPricing?.();
        throw new Error(data.error || "Enable failed");
      }
      showToast(data.warning || "IndexNow enabled for this storefront", data.warning ? "warning" : "success");
      if (isInstantSiteAuthError(data.warning)) {
        setInstantSiteHint(data.warning);
      }
      await refreshAll();
    } catch (err) {
      const msg = err.message || "Enable failed";
      showToast(msg, "error");
      if (isInstantSiteAuthError(msg)) {
        setInstantSiteHint(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (nextEnabled) => {
    if (!allowed && nextEnabled) { onOpenPricing?.(); return; }
    setBusy(true);
    try {
      const res = await api(nextEnabled ? "/enable" : "/disable", { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        if (data.upgradeRequired) onOpenPricing?.();
        throw new Error(data.error || (nextEnabled ? "Enable failed" : "Disable failed"));
      }
      showToast(
        nextEnabled
          ? (data.warning || "IndexNow enabled for this storefront")
          : "IndexNow disabled",
        nextEnabled && data.warning ? "warning" : "success",
      );
      if (nextEnabled && isInstantSiteAuthError(data.warning)) {
        setInstantSiteHint(data.warning);
      }
      await refreshAll();
    } catch (err) {
      const msg = err.message || "Update failed";
      showToast(msg, "error");
      if (isInstantSiteAuthError(msg)) {
        setInstantSiteHint(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    if (!allowed) { onOpenPricing?.(); return; }
    setBusy(true);
    try {
      const res = await api("/test", { method: "POST", body: "{}" });
      const data = await res.json();
      if (!res.ok) {
        if (data.upgradeRequired) onOpenPricing?.();
        throw new Error(data.error || "Test failed");
      }
      const msg = data.success
        ? `Test OK — key check HTTP ${data.keyCheck?.status}, submit HTTP ${data.submit?.status}`
        : `Test issue — key OK=${data.keyCheck?.ok}, submit: ${data.submit?.error || data.submit?.status}`;
      showToast(msg, data.success ? "success" : "warning");
      await refreshAll();
    } catch (err) {
      const msg = err.message || "Test failed";
      showToast(msg, "error");
      if (isInstantSiteAuthError(msg)) {
        setInstantSiteHint(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRebuild = async () => {
    if (!allowed) { onOpenPricing?.(); return; }
    setBusy(true);
    try {
      const res = await api("/rebuild", { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        if (data.upgradeRequired) onOpenPricing?.();
        throw new Error(data.error || "Rebuild failed");
      }
      showToast(data.message || "Queue rebuilt.", "success");
      setTab("queued");
      await loadQueue();
    } catch (err) {
      showToast(err.message || "Rebuild failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveSelected = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const res = await api("/queue", {
        method: "DELETE",
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Remove failed");
      showToast(`Removed ${data.removed || selectedIds.length} URL(s)`, "success");
      await loadQueue();
    } catch (err) {
      showToast(err.message || "Remove failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleManualSubmit = async (flushNow) => {
    if (!allowed) { onOpenPricing?.(); return; }
    const urls = manualText.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!urls.length) { showToast("Enter at least one URL", "warning"); return; }
    setBusy(true);
    try {
      const res = await api("/submit-manual", {
        method: "POST",
        body: JSON.stringify({ urls, flushNow }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.upgradeRequired) onOpenPricing?.();
        throw new Error(data.error || "Submit failed");
      }
      const rejected = Array.isArray(data.rejected) ? data.rejected.length : 0;
      showToast(`Queued ${data.accepted || 0} URL(s)${rejected ? `, ${rejected} rejected` : ""}`, "success");
      setShowManual(false);
      setManualText("");
      setTab("queued");
      await loadQueue();
    } catch (err) {
      showToast(err.message || "Submit failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleSelectAll = () =>
    setSelectedIds(selectedIds.length === queued.length ? [] : queued.map((r) => r.id));

  if (loading && !settings) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5 text-muted">Loading IndexNow…</div>
      </div>
    );
  }

  const storefrontLabel = settings?.storefrontHostname || settings?.storefrontOrigin || "Primary storefront";
  const tone = statusTone(settings?.status);
  const heroLabel =
    settings?.status === "active" ? "Active" :
    settings?.status === "error" ? "Needs attention" :
    settings?.status === "disabled" ? "Disabled" : "Not set up";

  return (
    <div className="w-100">
      <InstantSiteReconnectBanner
        forceShow={
          isInstantSiteAuthError(instantSiteHint) ||
          isInstantSiteAuthError(settings?.lastError)
        }
        hintError={instantSiteHint || settings?.lastError}
      />

      {/* Hero */}
      <div className="card border-0 shadow-sm mb-3" style={{ background: "linear-gradient(135deg, #ea580c14 0%, #ffffff 42%)" }}>
        <div className="card-body p-4">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-center gap-3">
              <span style={{ width: 48, height: 48, borderRadius: 12, background: "#ea580c20", color: "#ea580c", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Zap size={22} />
              </span>
              <div>
                <div className="text-uppercase fw-semibold small" style={{ color: "#ea580c", letterSpacing: "0.04em" }}>Search Engine Indexing</div>
                <h4 className="fw-bold mb-0">IndexNow</h4>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className={`badge bg-${tone}`} style={{ fontSize: "0.8rem", padding: "0.4em 0.75em" }}>{heroLabel}</span>
              {!settings?.enabled ? (
                <button type="button" className="btn btn-dark" onClick={handleEnable} disabled={busy || !allowed} title={allowed ? undefined : "Available on paid plans"}>
                  Enable IndexNow
                </button>
              ) : (
                <button type="button" className="btn btn-outline-primary" onClick={handleTest} disabled={busy || !allowed}>
                  Test Setup
                </button>
              )}
            </div>
          </div>
          <p className="text-muted mb-3" style={{ maxWidth: 680 }}>
            Push storefront URL updates to participating search engines whenever products and categories change.
          </p>

          {/* Metrics */}
          <div className="row g-3">
            {[
              { label: "Queued URLs", value: queuedTotal, hint: "Ready for the next batch" },
              { label: "Submitted URLs", value: submittedTotal, hint: "Unique URLs, recent history" },
              { label: "Storefront", value: storefrontLabel, hint: settings?.enabled ? "Connected and monitoring" : "Not enabled yet", small: true },
              {
                label: "Last Submitted",
                value: settings?.lastSubmittedAt ? new Date(settings.lastSubmittedAt).toLocaleString() : "Never",
                hint: "Batched through IndexNow",
                small: true,
              },
            ].map(({ label, value, hint, small }) => (
              <div key={label} className="col-sm-6 col-xl-3">
                <div className="card border-0 h-100" style={{ background: "rgba(255,255,255,0.75)" }}>
                  <div className="card-body p-3">
                    <div className="text-uppercase small fw-semibold text-muted mb-1" style={{ letterSpacing: "0.04em", fontSize: "0.7rem" }}>{label}</div>
                    <div className={`fw-bold mb-1 ${small ? "fs-6" : "fs-3"}`} style={{ lineHeight: 1.1 }}>{value}</div>
                    <div className="small text-muted">{hint}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Left: URL tables */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <ul className="nav nav-tabs mb-3">
                {["queued", "submitted"].map((t) => (
                  <li key={t} className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${tab === t ? "active" : ""}`}
                      onClick={() => setTab(t)}
                    >
                      {t === "queued" ? "Queued URLs" : "Submitted URLs"}
                      {t === "queued" && queuedTotal > 0 && (
                        <span className="badge bg-secondary ms-2">{queuedTotal}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Action bar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => refreshAll()} disabled={busy || loading}>
                    <RefreshCw size={13} className="me-1" />Refresh
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowManual(true)} disabled={busy || !allowed} title={allowed ? undefined : "Available on paid plans"}>
                    <Plus size={13} className="me-1" />Submit URLs Manually
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleRebuild} disabled={busy || !settings?.enabled || !allowed}>
                    Rebuild Queue
                  </button>
                  {tab === "queued" && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleRemoveSelected} disabled={busy || !selectedIds.length}>
                      <Trash2 size={13} className="me-1" />Remove Selected
                    </button>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 small text-muted">
                  <span>Show</span>
                  <select className="form-select form-select-sm" style={{ width: "auto" }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                    {[25, 50, 100].map((n) => <option key={n} value={n}>{n} items</option>)}
                  </select>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      {tab === "queued" && (
                        <th style={{ width: 36 }}>
                          <input type="checkbox" checked={queued.length > 0 && selectedIds.length === queued.length} onChange={toggleSelectAll} aria-label="Select all" />
                        </th>
                      )}
                      <th>URL</th>
                      <th style={{ width: "18%" }}>{tab === "queued" ? "Source" : "Status"}</th>
                      <th style={{ width: "22%" }}>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tab === "queued" && queued.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-5 text-muted">
                          <Zap size={28} className="mb-2 d-block mx-auto" style={{ opacity: 0.25 }} />
                          <div className="fw-semibold text-dark mb-1">No queued URLs</div>
                          <div className="small">New or rebuilt catalog URLs will appear here before submission.</div>
                        </td>
                      </tr>
                    )}
                    {tab === "queued" && queued.map((row) => (
                      <tr key={row.id}>
                        <td><input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)} aria-label={`Select ${row.url}`} /></td>
                        <td className="text-break"><a href={row.url} target="_blank" rel="noreferrer">{row.url}</a></td>
                        <td>{sourceLabel(row.reason)}</td>
                        <td className="text-muted small">{new Date(row.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                    {tab === "submitted" && submitted.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center py-5 text-muted">
                          <Zap size={28} className="mb-2 d-block mx-auto" style={{ opacity: 0.25 }} />
                          <div className="fw-semibold text-dark mb-1">No submitted URLs</div>
                          <div className="small">Submission history will appear here after the next IndexNow batch.</div>
                        </td>
                      </tr>
                    )}
                    {tab === "submitted" && submitted.map((row, idx) => (
                      <tr key={`${row.submissionId}-${idx}`}>
                        <td className="text-break"><a href={row.url} target="_blank" rel="noreferrer">{row.url}</a></td>
                        <td>
                          {row.error
                            ? <span className="text-danger">Error</span>
                            : <span className="text-success">HTTP {row.httpStatus ?? "—"}</span>}
                        </td>
                        <td className="text-muted small">{new Date(row.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 small text-muted">
                <span>Page {page + 1} of {pageCount}</span>
                <div className="btn-group btn-group-sm">
                  <button type="button" className="btn btn-outline-secondary" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</button>
                  <button type="button" className="btn btn-outline-secondary" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Plan + Settings + Info */}
        <div className="col-lg-4">
          {/* Plan card */}
          {/* <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="fw-semibold mb-1">{planLabel(accountType, allowed)}</div>
              {!allowed ? (
                <>
                  <p className="small text-muted mb-2">IndexNow is available on paid plans. Upgrade to enable automatic and manual URL submissions.</p>
                  <button type="button" className="btn btn-primary btn-sm w-100" onClick={() => onOpenPricing?.()}>Upgrade</button>
                </>
              ) : (
                <p className="small text-muted mb-0">Automatic notifications are included on your plan for this store.</p>
              )}
            </div>
          </div> */}

          {/* Settings card */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 fw-semibold mb-3">IndexNow Settings</h2>
              <div className="small text-muted mb-2">Indexed Storefront</div>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="indexnow-storefront"
                  checked={Boolean(settings?.enabled)}
                  disabled={busy || (!allowed && !settings?.enabled)}
                  onChange={(e) => handleToggle(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="indexnow-storefront">
                  {storefrontLabel}
                </label>
              </div>
              {settings?.status === "error" && settings.lastError && (
                <div className="small text-danger mb-2">{settings.lastError}</div>
              )}
              {storefrontKeyUrl && settings?.enabled && (
                <div className="small mb-2">
                  <a href={storefrontKeyUrl} target="_blank" rel="noreferrer">Key file</a>
                  {settings.indexnowKeyMasked && (
                    <span className="text-muted"> ({settings.indexnowKeyMasked})</span>
                  )}
                </div>
              )}
              <div className="small text-muted">Changes apply immediately when you enable or disable.</div>
            </div>
          </div>

          {/* Info card */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-start gap-2">
                <Info size={16} className="text-secondary flex-shrink-0 mt-1" />
                <div className="small text-muted">
                  <strong className="text-dark d-block mb-1">How It Works</strong>
                  IndexNow is an open protocol from{" "}
                  <a href="https://www.indexnow.org/" target="_blank" rel="noreferrer">indexnow.org</a>{" "}
                  that lets websites instantly notify participating search engines when a URL is added, updated, or deleted. When enabled, this app monitors your store and sends URL updates automatically for products and categories.
                  <br /><br />
                  Supported search engines include Microsoft Bing, Yandex, Naver, Seznam.cz, and Yep. A single submission is shared across the IndexNow ecosystem.
                  <br /><br />
                  Google does not currently support IndexNow, so normal SEO foundations remain important.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Manual submit modal */}
      {showManual && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card border-0 shadow" style={{ width: "min(520px, 95vw)" }}>
            <div className="card-body">
              <h2 className="h5 fw-semibold mb-2">Submit URLs Manually</h2>
              <p className="small text-muted mb-3">
                One URL per line. Host must match <code>{settings?.storefrontHostname || "your storefront"}</code>.
              </p>
              <textarea
                className="form-control mb-3"
                rows={8}
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                placeholder="https://www.example.com/product-a/"
              />
              <div className="d-flex flex-wrap justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowManual(false)} disabled={busy}>Cancel</button>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => handleManualSubmit(false)} disabled={busy}>Add to Queue</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => handleManualSubmit(true)} disabled={busy}>Submit Now</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
